import { useLocation } from "@reach/router";
import { graphql, navigate, useStaticQuery } from "gatsby";
import { useTranslation } from "gatsby-plugin-react-i18next";
import {
	useCallback,
	useEffect,
	useRef,
	useState,
	type ChangeEvent,
} from "react";
import QuickSearchBar from "../../components/Search/components/QuickSearchBar/QuickSearchBar";
import { AppDispatchContext, useAppState } from "../../context/app/context";
import type { ActiveSearchType } from "../../context/app/reducer";
import type { SearchResultCMS } from "../../hooks/search/types";
import { useSearch } from "../../hooks/search/use-search";
import { useDispatchContext } from "../../hooks/use-dispatch-context";
import { useTranslatedPaths } from "../../hooks/use-translated-paths";
import { formatPrice } from "../../utils/cart/currency";
import { variantName } from "../../utils/storyblok/utils";
import QuickSearchComponent, {
	type QuickSearchProductProps,
} from "./quick-search.component";

const QuickSearchContainer = () => {
	const dispatch = useDispatchContext(AppDispatchContext);
	const { searchOpen, activeSearchType } = useAppState();
	const location = useLocation();
	const { t } = useTranslation();
	const params = new URLSearchParams(location.search);
	const searchTerm = params.get("q") || "";
	const searchFormRef = useRef<HTMLFormElement | null>(null);

	const {
		search: productSearch,
		clear: productClear,
		productResults,
		query: productQuery,
		suggestionResults,
	} = useSearch({
		searchTerm,
		searchType: "product",
		limit: 3,
	});

	const {
		search: pagesSearch,
		clear: pagesClear,
		cmsResults: pagesResults,
		query: pagesQuery,
	} = useSearch({
		searchTerm,
		searchType: "page",
		limit: 2,
	});
	const [products, setProducts] = useState<QuickSearchProductProps[]>([]);
	const [pages, setPages] = useState<SearchResultCMS[]>([]);
	const [suggestions, setSuggestions] = useState<string[]>([]);
	const { searchPath } = useTranslatedPaths();

	const query = useStaticQuery<Queries.QuickSearchQuery>(graphql`
		query QuickSearch {
			page: storyblokEntry(field_component: { eq: "search_page" }) {
				quick_search_suggestions {
					...Bullet
				}
				shipping_delivery {
					...Link
				}
				faq {
					...Link
				}
				contact_us {
					...Link
				}
			}
		}
	`).page;

	const popularSearches =
		query?.quick_search_suggestions?.map(
			(suggestion) => suggestion?.text ?? "",
		) ?? [];

	const getHelpLinks = {
		faq: {
			target: query?.faq?.target ?? "",
			url: query?.faq?.cached_url ?? "",
		},
		contactUs: {
			target: query?.contact_us?.target ?? "",
			url: query?.contact_us?.cached_url ?? "",
		},
		shipping: {
			target: query?.shipping_delivery?.target ?? "",
			url: query?.shipping_delivery?.cached_url ?? "",
		},
	};

	const labels = {
		searchLabel: t("Search"),
		searchPlaceholder: t("Search for products, articles or pages"),
		clearLabel: t("Clear"),
		closeLabel: t("Close"),
		suggestionsLabel: t("Sugestions"),
		popularLabel: t("Popular Searches"),
		productsLabel: t("Products"),
		trendingLabel: t("Trending products"),
		pagesLabel: t("Pages"),
		showAllLabel: t("Show all"),
		getHelpLabel: t("Get help"),
		customerServiceLabel: t("Customer service"),
		shippingLabel: t("Shipping and delivery"),
		faqLabel: t("FAQ"),
	};

	const setIsSearchOpen = useCallback(
		(value: boolean) => {
			if (location.pathname.includes("search")) return;
			dispatch({
				type: "setSearchOpen",
				searchOpen: value,
			});
			dispatch({
				type: "setActiveSearchType",
				activeSearchType: "product",
			});
		},
		[dispatch, location.pathname],
	);

	const handleSearchInputChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const searchTerm = e.target.value;
			void productSearch(searchTerm, undefined, { type: "product" });
			void pagesSearch(searchTerm, undefined, { type: "page" });
		},
		[productSearch, pagesSearch],
	);

	const clearSearchResults = useCallback(() => {
		productClear();
		pagesClear();
	}, [pagesClear, productClear]);

	const resetUiHandler = () => {
		clearSearchResults();
		dispatch({
			type: "setActiveSearchType",
			activeSearchType: "product",
		});
	};

	const closeAndClearSearch = () => {
		setIsSearchOpen(false);
		resetUiHandler();
	};

	const setActivePage = () => {
		dispatch({
			type: "setActiveSearchType",
			activeSearchType: "page",
		});
		return void navigate(`${searchPath}?q=${encodeURIComponent(productQuery)}`);
	};

	const setActiveProduct = useCallback(() => {
		dispatch({
			type: "setActiveSearchType",
			activeSearchType: "product",
		});
		return void navigate(`${searchPath}?q=${encodeURIComponent(productQuery)}`);
	}, [dispatch, productQuery, searchPath]);

	const setActiveSearchType = (selectedOption: ActiveSearchType) => {
		dispatch({
			type: "setActiveSearchType",
			activeSearchType: selectedOption,
		});
	};

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (!searchOpen) return; // TODO: Fix the quick search not to be execute on the search page
			if (e.key === "Escape") {
				setIsSearchOpen(false);
				clearSearchResults();
				dispatch({
					type: "setActiveSearchType",
					activeSearchType: "product",
				});
			}
			if (e.key === "Enter") {
				e.preventDefault();
				setIsSearchOpen(false);
				setActiveProduct();
			}
		};

		const handleSubmit = (e: Event) => {
			e.preventDefault();
			setIsSearchOpen(false);
			clearSearchResults();
			setActiveProduct();
		};

		window.addEventListener("keydown", handleKeyDown);

		const currentSearchForm = searchFormRef.current;
		if (currentSearchForm) {
			currentSearchForm.addEventListener("submit", handleSubmit);
		}

		return () => {
			window.removeEventListener("keydown", handleKeyDown);
			if (currentSearchForm) {
				currentSearchForm.removeEventListener("submit", handleSubmit);
			}
		};
	}, [
		setIsSearchOpen,
		dispatch,
		clearSearchResults,
		searchOpen,
		setActiveProduct,
	]);

	useEffect(() => {
		setPages(
			pagesResults.map((page) => {
				return {
					id: page.id,
					image: page.image,
					name: page.name,
					shortDesc: page.shortDesc,
					url: page.url,
				};
			}),
		);
	}, [pagesResults]);

	useEffect(() => {
		setSuggestions(suggestionResults.map((suggestion) => suggestion.suggest));
	}, [suggestionResults]);

	useEffect(() => {
		if (productQuery || pagesQuery) {
			setIsSearchOpen(true);
		}
	}, [productQuery, pagesQuery, setIsSearchOpen]);

	useEffect(() => {
		setProducts(
			productResults
				.filter((results) => results.price)
				.map((product) => {
					return {
						uid: product.id,
						productName: product.name,
						productImage: {
							filename: product.image,
							alt: `${t("Image of")} ${product.name}`,
						},
						price: formatPrice(
							product.currency,
							Number.parseFloat(product.price),
						),
						productLink: product.url,
						variantLabel: variantName(product.color, product.fabric),
					};
				}),
		);
	}, [t, productResults]);

	useEffect(() => {
		const handleTouchMove = () => {
			const activeElement = document.activeElement as HTMLElement;
			if (
				activeElement.tagName === "INPUT" ||
				activeElement.tagName === "TEXTAREA"
			) {
				activeElement.blur();
			}
		};

		if (searchOpen) {
			window.scrollTo({
				top: 0,
				behavior: "smooth",
			});
			document.body.style.overflowY = "hidden";
			window.addEventListener("touchmove", handleTouchMove);
		}

		return () => {
			document.body.style.overflowY = "";
			window.removeEventListener("touchmove", handleTouchMove);
		};
	}, [searchOpen]);

	return (
		<div>
			<QuickSearchBar
				setShowSearch={() => setIsSearchOpen(!searchOpen)}
				setSearchValue={handleSearchInputChange}
				clearCallback={clearSearchResults}
				searchValue={productQuery}
				isOpen={searchOpen}
				labels={labels}
			/>
			<QuickSearchComponent
				activeSearchType={activeSearchType}
				resetUiHandler={resetUiHandler}
				searchValue={productQuery}
				setSearchValue={handleSearchInputChange}
				closeAndClearSearch={closeAndClearSearch}
				isSearchOpen={searchOpen}
				pages={pagesQuery ? pages : []}
				products={products}
				searchList={productQuery ? suggestions : popularSearches}
				labels={labels}
				setActivePage={setActivePage}
				setActiveSearchType={setActiveSearchType}
				setActiveProduct={setActiveProduct}
				getHelpLinks={getHelpLinks}
			/>
		</div>
	);
};

export default QuickSearchContainer;
